import { useMemo } from 'react';

import {
  PermissionGroupCreateRequestDto,
  PermissionGroupUpdateRequestDto,
  UserCreateRequestDto,
  UserUpdateRequestDto,
  usePermissionGroupCreateControllerCreateForManagerMutation,
  usePermissionGroupCreateControllerCreateMutation,
  usePermissionGroupDeleteControllerDeleteForManagerMutation,
  usePermissionGroupDeleteControllerDeleteMutation,
  usePermissionGroupListControllerListForManagerQuery,
  usePermissionGroupListControllerListQuery,
  usePermissionGroupUpdateControllerUpdateForManagerMutation,
  usePermissionGroupUpdateControllerUpdateMutation,
  useUserListControllerListForManagerQuery,
  useUserListControllerListQuery,
  useUserCreateControllerCreateUserMutation,
  useUserMeControllerUserMeQuery,
  useUserUpdateControllerUpdateMutation,
  useUserPermissionGroupAttachControllerAttachForManagerMutation,
  useUserPermissionGroupAttachControllerAttachMutation,
  useUserPermissionGroupDetachControllerDetachForManagerMutation,
  useUserPermissionGroupDetachControllerDetachMutation,
} from '@shared/services/apiService';

import type { ActorPrivileges } from './privileges';

/**
 * One surface over the two audiences of the same API: a company user managing their own
 * company (`v2/...`), or a platform admin managing `managedCompanyId` (`v2/admin/companies/:id/...`).
 * The admin routes skip anti-escalation, so `actorPrivileges` is `null` there.
 */
export const useAccessControlApi = (managedCompanyId?: string) => {
  const isManager = managedCompanyId !== undefined;
  const id = managedCompanyId ?? '';

  const me = useUserMeControllerUserMeQuery(undefined, { skip: isManager });
  const actorPrivileges: ActorPrivileges = isManager ? null : (me.data?.privileges ?? []);
  const canManageUsers = actorPrivileges === null || actorPrivileges.includes('user.manage');
  const canManageGroups = actorPrivileges === null || actorPrivileges.includes('group.manage');

  const companyGroups = usePermissionGroupListControllerListQuery(undefined, { skip: isManager });
  const managerGroups = usePermissionGroupListControllerListForManagerQuery(
    { id },
    { skip: !isManager },
  );
  const companyUsers = useUserListControllerListQuery(undefined, {
    skip: isManager || !canManageUsers,
  });
  const managerUsers = useUserListControllerListForManagerQuery({ id }, { skip: !isManager });

  const [createForCompany] = usePermissionGroupCreateControllerCreateMutation();
  const [createForManager] = usePermissionGroupCreateControllerCreateForManagerMutation();
  const [updateForCompany] = usePermissionGroupUpdateControllerUpdateMutation();
  const [updateForManager] = usePermissionGroupUpdateControllerUpdateForManagerMutation();
  const [deleteForCompany] = usePermissionGroupDeleteControllerDeleteMutation();
  const [deleteForManager] = usePermissionGroupDeleteControllerDeleteForManagerMutation();
  const [attachForCompany] = useUserPermissionGroupAttachControllerAttachMutation();
  const [attachForManager] = useUserPermissionGroupAttachControllerAttachForManagerMutation();
  const [detachForCompany] = useUserPermissionGroupDetachControllerDetachMutation();
  const [detachForManager] = useUserPermissionGroupDetachControllerDetachForManagerMutation();
  const [createUserForManager] = useUserCreateControllerCreateUserMutation();
  const [updateUserForManager] = useUserUpdateControllerUpdateMutation();

  const groups = isManager ? managerGroups : companyGroups;
  const users = isManager ? managerUsers : companyUsers;

  const actions = useMemo(
    () => ({
      createGroup: (body: PermissionGroupCreateRequestDto) =>
        (isManager
          ? createForManager({ id, permissionGroupCreateRequestDto: body })
          : createForCompany({ permissionGroupCreateRequestDto: body })
        ).unwrap(),
      updateGroup: (groupId: string, body: PermissionGroupUpdateRequestDto) =>
        (isManager
          ? updateForManager({ id, groupId, permissionGroupUpdateRequestDto: body })
          : updateForCompany({ groupId, permissionGroupUpdateRequestDto: body })
        ).unwrap(),
      deleteGroup: (groupId: string) =>
        (isManager ? deleteForManager({ id, groupId }) : deleteForCompany({ groupId })).unwrap(),
      attach: (userId: string, groupId: string) =>
        (isManager
          ? attachForManager({ id, userId, groupId })
          : attachForCompany({ userId, groupId })
        ).unwrap(),
      detach: (userId: string, groupId: string) =>
        (isManager
          ? detachForManager({ id, userId, groupId })
          : detachForCompany({ userId, groupId })
        ).unwrap(),
      // Accounts are made and edited by the platform admin only; there are no company routes.
      createUser: (body: Omit<UserCreateRequestDto, 'companyId'>) =>
        createUserForManager({ userCreateRequestDto: { ...body, companyId: id } }).unwrap(),
      updateUser: (userId: string, body: UserUpdateRequestDto) =>
        updateUserForManager({ id: userId, userUpdateRequestDto: body }).unwrap(),
    }),
    [
      isManager,
      id,
      createForCompany,
      createForManager,
      updateForCompany,
      updateForManager,
      deleteForCompany,
      deleteForManager,
      attachForCompany,
      attachForManager,
      detachForCompany,
      detachForManager,
      createUserForManager,
      updateUserForManager,
    ],
  );

  return {
    // The platform admin isn't a company user, so every user of the company is someone else.
    actorId: isManager ? null : (me.data?.id ?? null),
    actorPrivileges,
    canManageUsers,
    canManageGroups,
    /** Create users and change their name, password and base access. */
    canManageAccounts: isManager,
    isActorLoading: !isManager && me.isLoading,
    groups: groups.data ?? [],
    isGroupsLoading: groups.isLoading,
    users: users.data ?? [],
    isUsersLoading: users.isLoading,
    ...actions,
  };
};

export type AccessControlApi = ReturnType<typeof useAccessControlApi>;
